/**
 * OEM Presence — element-anchored live presence (OpenEye Marketing).
 *
 * Instead of drawing peers' raw cursor coordinates (which can't map between a
 * phone and a desktop — different layouts, different perspective), presence is
 * anchored to the DOM: we broadcast WHICH product/element a peer is looking at
 * or clicking, and glow that same element locally. A product card someone else
 * is viewing glows on every device; a button someone clicks pulses. This is
 * viewport-proof and works on mobile ↔ desktop.
 *
 * The pill says only that others are here — never a count.
 *
 * Wire protocol (JSON):
 *   {t:'hi',   id}          join / heartbeat (drives the pill)
 *   {t:'view', id, ref}     peer is now looking at element `ref` ('' = nothing)
 *   {t:'tap',  id, ref}     peer clicked element `ref`
 *   {t:'bye',  id}          leave
 *
 * `ref` is a stable, layout-independent key: 'p:<product-handle>' for products,
 * else '#<id>'. Elements with no stable key simply don't glow.
 */

const HEARTBEAT_MS = 10_000;
const PEER_TTL_MS = 30_000;
const VIEW_INTERVAL_MS = 120;
const MAX_BACKOFF_MS = 30_000;

function freshId() {
  // Fresh per page load — persisted ids made duplicated tabs filter each
  // other out as "self".
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable, cross-viewport key for an element (or null if it has none). */
function refFor(node) {
  if (!(node instanceof Element)) return null;
  const productLink = node.closest('a[href*="/products/"]');
  if (productLink) {
    const match = productLink.getAttribute('href').match(/\/products\/([^/?#]+)/);
    if (match) return `p:${match[1]}`;
  }
  const pid = node.closest('[data-product-id]');
  if (pid && pid.getAttribute('data-product-id')) return `pid:${pid.getAttribute('data-product-id')}`;
  const withId = node.closest('[id]');
  if (withId && withId.id && !withId.id.startsWith('shopify-section')) return `#${withId.id}`;
  return null;
}

// Presence glow belongs on main browsing pages only — never inside the cart
// drawer, header, sticky bar, nudges, or the raffle/upsell UI (the same product
// appears in those and would light up everywhere someone touched one item).
const OEM_PRESENCE_EXCLUDE =
  'cart-drawer-component, #cart-drawer, .cart-drawer, .cart-items, header, .header-section, oem-cart-upsell, .oem-upsell, oem-nudge, oem-raffle, oem-sticky-atc, [data-oem-nudge-product]';

/** Product-card elements (main content only) that a ref points at. */
function elementsForRef(ref) {
  if (!ref) return [];
  const keep = (el) => el instanceof Element && !el.closest(OEM_PRESENCE_EXCLUDE);

  if (ref.startsWith('p:')) {
    const handle = ref.slice(2);
    const targets = new Set();
    document.querySelectorAll(`a[href*="/products/${handle}"]`).forEach((link) => {
      const card = link.closest('.product-card');
      if (card && keep(card)) targets.add(card);
    });
    return [...targets];
  }
  if (ref.startsWith('pid:')) {
    return [...document.querySelectorAll(`.product-card[data-product-id="${ref.slice(4)}"]`)].filter(keep);
  }
  if (ref.startsWith('#')) {
    const el = document.getElementById(ref.slice(1));
    return el && keep(el) ? [el] : [];
  }
  return [];
}

class OEMPresence extends HTMLElement {
  #ws = null;
  #me = freshId();
  #peers = new Map(); // id -> { lastSeen, ref }
  #viewers = new Map(); // element -> Set(peerId)  (for soft "viewing" glow)
  #pill = null;
  #heartbeatTimer = 0;
  #viewTimer = 0;
  #sweepTimer = 0;
  #backoff = 1000;
  #closed = false;
  #lastSentRef = null;
  #pendingRef = null;
  #reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  #isCoarse = matchMedia('(pointer: coarse)').matches;

  connectedCallback() {
    const mode = this.getAttribute('mode') || 'off';
    if (mode === 'off') return;

    const isDemo = mode === 'demo';
    const demoAllowed =
      (window.Shopify && window.Shopify.designMode) || location.search.includes('oem-presence-demo');
    if (isDemo && !demoAllowed) return;

    const start = () => (isDemo ? this.#startDemo() : this.#connect());
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 4000 });
    else setTimeout(start, 2500);
  }

  disconnectedCallback() {
    this.#closed = true;
    clearInterval(this.#heartbeatTimer);
    clearInterval(this.#viewTimer);
    clearInterval(this.#sweepTimer);
    if (this.#ws && this.#ws.readyState <= WebSocket.OPEN) {
      try {
        this.#ws.send(JSON.stringify({ t: 'bye', id: this.#me }));
      } catch (e) {
        /* ignore */
      }
      this.#ws.close();
    }
  }

  /* ---------- pill ---------- */

  #ensurePill() {
    if (this.#pill || !this.hasAttribute('show-pill')) return;
    this.#pill = document.createElement('div');
    this.#pill.className = 'oem-presence__pill';
    this.#pill.hidden = true;
    this.appendChild(this.#pill);
  }

  #updatePill() {
    const others = this.#peers.size;
    const label =
      others === 1
        ? '\u{1F440} someone else is browsing with you'
        : '\u{1F440} others are browsing with you';
    if (this.#pill) {
      this.#pill.hidden = others < 1;
      if (others >= 1) this.#pill.textContent = label;
    }
    document.querySelectorAll('[data-oem-viewers]').forEach((el) => {
      el.hidden = others < 1;
      if (others >= 1) {
        el.textContent =
          others === 1 ? 'someone else is viewing this right now' : 'others are viewing this right now';
      }
    });
  }

  /* ---------- glow application ---------- */

  #setPeerView(id, ref) {
    const peer = this.#peers.get(id) || { lastSeen: Date.now(), ref: null };
    // remove old glow
    if (peer.ref) this.#removeViewer(peer.ref, id);
    peer.ref = ref || null;
    peer.lastSeen = Date.now();
    this.#peers.set(id, peer);
    if (peer.ref) this.#addViewer(peer.ref, id);
    this.#updatePill();
  }

  #addViewer(ref, id) {
    for (const el of elementsForRef(ref)) {
      let set = this.#viewers.get(el);
      if (!set) {
        set = new Set();
        this.#viewers.set(el, set);
      }
      set.add(id);
      el.classList.add('oem-presence-viewing');
    }
  }

  #removeViewer(ref, id) {
    for (const el of elementsForRef(ref)) {
      const set = this.#viewers.get(el);
      if (!set) continue;
      set.delete(id);
      if (set.size === 0) {
        el.classList.remove('oem-presence-viewing');
        this.#viewers.delete(el);
      }
    }
  }

  #pulse(ref) {
    if (this.#reducedMotion) return;
    for (const el of elementsForRef(ref)) {
      el.classList.remove('oem-presence-tap');
      // reflow so the animation restarts even on repeat taps
      void el.offsetWidth;
      el.classList.add('oem-presence-tap');
      setTimeout(() => el.classList.remove('oem-presence-tap'), 900);
    }
  }

  #dropPeer(id) {
    const peer = this.#peers.get(id);
    if (peer && peer.ref) this.#removeViewer(peer.ref, id);
    this.#peers.delete(id);
    this.#updatePill();
  }

  /* ---------- connection ---------- */

  #connect() {
    const endpoint = this.getAttribute('endpoint');
    if (!endpoint || !endpoint.startsWith('wss://') || this.#closed) return;
    const room = encodeURIComponent(this.getAttribute('room') || 'global');

    let socket;
    try {
      socket = new WebSocket(`${endpoint.replace(/\/$/, '')}/${room}`);
    } catch (e) {
      return;
    }
    this.#ws = socket;

    socket.addEventListener('open', () => {
      this.#backoff = 1000;
      this.#ensurePill();
      this.#send({ t: 'hi', id: this.#me });
      this.#bindInput();
      this.#heartbeatTimer = setInterval(() => this.#send({ t: 'hi', id: this.#me }), HEARTBEAT_MS);
      this.#sweepTimer = setInterval(() => this.#sweep(), PEER_TTL_MS / 3);
    });

    socket.addEventListener('message', (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch (e) {
        return;
      }
      if (!msg || msg.id === this.#me) return;
      switch (msg.t) {
        case 'hi':
          if (!this.#peers.has(msg.id)) this.#peers.set(msg.id, { lastSeen: Date.now(), ref: null });
          else this.#peers.get(msg.id).lastSeen = Date.now();
          this.#updatePill();
          break;
        case 'view':
          this.#setPeerView(msg.id, msg.ref);
          break;
        case 'tap':
          this.#setPeerView(msg.id, msg.ref); // keeps peer alive + glows target
          this.#pulse(msg.ref);
          break;
        case 'bye':
          this.#dropPeer(msg.id);
          break;
      }
    });

    socket.addEventListener('close', () => {
      clearInterval(this.#heartbeatTimer);
      clearInterval(this.#viewTimer);
      clearInterval(this.#sweepTimer);
      if (this.#closed) return;
      setTimeout(() => this.#connect(), this.#backoff);
      this.#backoff = Math.min(this.#backoff * 2, MAX_BACKOFF_MS);
    });

    socket.addEventListener('error', () => socket.close());
  }

  #send(obj) {
    if (this.#ws && this.#ws.readyState === WebSocket.OPEN && !document.hidden) {
      this.#ws.send(JSON.stringify(obj));
    }
  }

  #bindInput() {
    // Everyone (mouse OR touch) broadcasts what they touch/click.
    document.addEventListener(
      'pointerdown',
      (event) => {
        const ref = refFor(event.target);
        if (ref) this.#send({ t: 'tap', id: this.#me, ref });
      },
      { passive: true }
    );

    // Touch devices have no hover — broadcast the product nearest the viewport
    // centre so other shoppers (including other phones) can see what a mobile
    // visitor is looking at, not just their taps.
    if (this.#isCoarse) {
      this.#viewTimer = setInterval(() => {
        const ref = this.#centeredProductRef();
        if (ref !== this.#lastSentRef) {
          this.#lastSentRef = ref;
          this.#send({ t: 'view', id: this.#me, ref });
        }
      }, 450);
      return;
    }

    // Desktop: hovering broadcasts what's being viewed.
    document.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
        this.#pendingRef = refFor(event.target) || '';
      },
      { passive: true }
    );

    this.#viewTimer = setInterval(() => {
      if (this.#pendingRef !== null && this.#pendingRef !== this.#lastSentRef) {
        this.#lastSentRef = this.#pendingRef;
        this.#send({ t: 'view', id: this.#me, ref: this.#pendingRef });
      }
      this.#pendingRef = null;
    }, VIEW_INTERVAL_MS);
  }

  #centeredProductRef() {
    const cy = window.innerHeight / 2;
    let best = '';
    let bestDist = Infinity;
    document.querySelectorAll('.product-card').forEach((card) => {
      if (card.closest(OEM_PRESENCE_EXCLUDE)) return;
      const r = card.getBoundingClientRect();
      if (r.bottom < 0 || r.top > window.innerHeight) return;
      const d = Math.abs((r.top + r.bottom) / 2 - cy);
      if (d < bestDist) {
        bestDist = d;
        const link = card.querySelector('a[href*="/products/"]');
        best = refFor(link || card) || '';
      }
    });
    return best;
  }

  #sweep() {
    const now = Date.now();
    for (const [id, peer] of this.#peers) {
      if (now - peer.lastSeen > PEER_TTL_MS) this.#dropPeer(id);
    }
  }

  /* ---------- demo (editor / ?oem-presence-demo) ---------- */

  #startDemo() {
    this.#ensurePill();
    this.#peers.set('demo-1', { lastSeen: Date.now(), ref: null });
    this.#peers.set('demo-2', { lastSeen: Date.now(), ref: null });
    this.#updatePill();

    const cards = [...document.querySelectorAll('.product-card, a[href*="/products/"]')].slice(0, 8);
    if (!cards.length) return;
    let i = 0;
    const cycle = () => {
      const el = cards[i % cards.length];
      const ref = refFor(el);
      i++;
      if (ref) {
        this.#setPeerView('demo-1', ref);
        if (i % 3 === 0) this.#pulse(ref);
      }
      this.#viewTimer = setTimeout(cycle, 1600);
    };
    cycle();
  }
}

if (!customElements.get('oem-presence')) {
  customElements.define('oem-presence', OEMPresence);
}
